/**
 * Hook for managing patient search configuration
 * Stores which fields and actions to show in search results
 */

import { useState, useEffect } from 'react';
import { storage } from '@/utils/storage';

const SEARCH_CONFIG_KEY = 'pp_patientSearchConfig';

export interface PatientSearchConfig {
  fields: {
    showMRN: boolean;
    showDOB: boolean;
    showPhone: boolean;
  };
  actions: {
    view: boolean;
    video: boolean;
    message: boolean;
    orderDevice: boolean;
    follow: boolean;
  };
}

const DEFAULT_CONFIG: PatientSearchConfig = {
  fields: {
    showMRN: true,
    showDOB: true,
    showPhone: true,
  },
  actions: {
    view: true,
    video: true,
    message: false,
    orderDevice: false,
    follow: false,
  },
};

export const usePatientSearchConfig = () => {
  const [config, setConfig] = useState<PatientSearchConfig>(() => {
    const stored = storage.get<PatientSearchConfig>(SEARCH_CONFIG_KEY);
    if (!stored) {
      return DEFAULT_CONFIG;
    }
    // Merge with defaults to handle missing fields
    return {
      fields: {
        ...DEFAULT_CONFIG.fields,
        ...(stored.fields || {}),
      },
      actions: {
        ...DEFAULT_CONFIG.actions,
        ...(stored.actions || {}),
      },
    };
  });

  const updateConfig = (newConfig: Partial<PatientSearchConfig>) => {
    const updated = {
      ...config,
      ...newConfig,
      fields: {
        ...config.fields,
        ...(newConfig.fields || {}),
      },
      actions: {
        ...config.actions,
        ...(newConfig.actions || {}),
      },
    };
    setConfig(updated);
    storage.set(SEARCH_CONFIG_KEY, updated);
  };

  const resetToDefaults = () => {
    setConfig(DEFAULT_CONFIG);
    storage.set(SEARCH_CONFIG_KEY, DEFAULT_CONFIG);
  };

  return {
    config,
    updateConfig,
    resetToDefaults,
  };
};

